import { apiClient } from "@config/apiClient";
import {
  ICompany,
  ICompanyPriority,
  ICompanyStage,
  ICompanyType,
  ICreateCompanyFromExternal,
  ICreatedCompanyFromExternalResponse,
  ISuggestionCompany,
} from "@interfaces/company.interface";
import { CreateCompanyRequest } from "@app-types/requests.type";
import { IPagedResponse } from "@interfaces/responses.interface";
import { API_URL } from "@config/constants";

class CompanyService {
  private URL = `${API_URL}/companies`;

  async getCompanies(params: URLSearchParams): Promise<IPagedResponse<ICompany>> {
    return apiClient.get(`${this.URL}/companies/?${params.toString()}`);
  }

  async getCompanyById(companyId: string): Promise<ICompany> {
    return apiClient.get(`${this.URL}/companies/${companyId}/`);
  }

  async getCompanyByWebsite(website: string): Promise<{ company_id: string | null }> {
    return apiClient.post(`${this.URL}/chrome-extension/get-company/`, { website });
  }

  async getCompanySuggestions(name: string): Promise<ISuggestionCompany[]> {
    return apiClient.get(`${this.URL}/companies/search/?name=${name}`);
  }

  async createCompany(data: CreateCompanyRequest): Promise<ICompany> {
    return apiClient.post(`${this.URL}/companies/`, data);
  }

  async updateCompany(
    companyId: string,
    data: Partial<CreateCompanyRequest>,
    skipRequiredAttributes = false
  ): Promise<ICompany> {
    const query = skipRequiredAttributes ? "?skip_required_attributes=True" : "";

    return apiClient.patch(`${this.URL}/companies/${companyId}/${query}`, data);
  }

  async addAlternativeDomain(companyId: string, website: string): Promise<void> {
    return apiClient.post(`${this.URL}/companies/${companyId}/alternative-domains/`, {
      website,
    });
  }

  async updateAlternativeDomain(
    companyId: string,
    oldWebsite: string,
    newWebsite: string
  ): Promise<void> {
    return apiClient.patch(`${this.URL}/companies/${companyId}/alternative-domains/`, {
      old_website: oldWebsite,
      new_website: newWebsite,
    });
  }

  async deleteAlternativeDomain(companyId: string, website: string): Promise<void> {
    return apiClient.delete(`${this.URL}/companies/${companyId}/alternative-domains/`, {
      data: { website },
    });
  }

  async createCompanyFromExternal(
    data: ICreateCompanyFromExternal
  ): Promise<ICreatedCompanyFromExternalResponse> {
    return apiClient.post(
      `${this.URL}/companies/create-from-external/?skip_required_attributes=True`,
      data
    );
  }

  async getCompanyStages(): Promise<IPagedResponse<ICompanyStage>> {
    return apiClient.get(`${this.URL}/stages/`);
  }

  async getCompanyPriorities(): Promise<IPagedResponse<ICompanyPriority>> {
    return apiClient.get(`${this.URL}/priorities/`);
  }

  async getCompanyTypes(): Promise<IPagedResponse<ICompanyType>> {
    return apiClient.get(`${this.URL}/types/`);
  }
}

const companyService = new CompanyService();
export default companyService;
